import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@morpho-org/ui-v6/server';
import { cn } from '@morpho-org/ui-v6/utils';

/**
 * Loading stand-ins for the vault recaps' rows. Each keeps the wrapper, gaps and line boxes of the
 * row it replaces, so a recap rendered from these inside `RecapContainer` is the same height and
 * shape as the loaded card and nothing below it moves when the vault data lands.
 */

/** A `text-xs` value: one shared line-height tall. */
export const RecapTextSkeleton = t0 => {
  const $ = _c(4);
  const {
    className: t1
  } = t0;
  const className = t1 === undefined ? "w-[56px]" : t1;
  let t2;
  if ($[0] !== className) {
    t2 = cn("h-(--text-xs--line-height)", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = <Skeleton className={t2} />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};

/** `RecapVaultHeader`: the 24px vault avatar and the name's `text-title-xs` line box. */
export const RecapVaultHeaderSkeleton = () => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton variant="circle" className="size-[24px]" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      lineHeight: "normal"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="gap-xs flex items-center">{t0}<span className="text-title-xs relative w-[160px]" style={t1}> <Skeleton className="absolute inset-x-0 top-1/2 h-[1em] -translate-y-1/2" /></span></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};

/** `RecapInteraction`: its label, then the amount row, which the 26px USD tag sets the height of. */
export const RecapInteractionSkeleton = t0 => {
  const $ = _c(5);
  const {
    label
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-secondary-foreground text-xs">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex items-center justify-between gap-[16px]"><Skeleton className="h-[26px] w-[140px]" /><Skeleton variant="circle" className="size-[24px]" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <div className="gap-sm pb-2xs flex flex-col items-stretch">{t1}{t2}</div>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};

/** A label/value row whose label also waits on the vault data (it names the asset). */
export const RecapPositionRowSkeleton = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex items-center justify-between gap-[16px]"><RecapTextSkeleton className="w-[96px]" /><RecapTextSkeleton className="w-[80px]" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};

/** The vault recaps' APY row. */
export const RecapApyRowSkeleton = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex items-center justify-between gap-[16px]"><span className="text-secondary-foreground text-xs">APY</span><RecapTextSkeleton className="w-[40px]" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};